import { Item, ItemParams, Menu, useContextMenu } from 'react-contexify';
import React, { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Avatar from 'react-avatar';
import { ReactComponent as DocumentIcon } from '../../assets/document.svg';
import { EanURL } from '../../common/eanUtils';
import { ReactComponent as ExitIcon } from '../../assets/exit.svg';
import NotificationsCounter from '../notification/NotificationsCounter';
import NotificationsPanel from '../notification/NotificationsPanel';
import { ReactComponent as ProfileIcon } from '../../assets/profile.svg';
import { ReactComponent as QuestionIcon } from '../../assets/question.svg';
import Skeleton from 'react-loading-skeleton';
import { routes } from '../../routes';
import { uiState } from '../../atoms/uiAtom';
import { useAuth } from '../../hooks/auth';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { userPreferencesState } from '../../atoms/userPreferencesAtom/userPreferencesAtom';
import visualLogo from '../../assets/visual-logo-white.svg';

const MENU_ACTIONS = {
  PROFILE: 'profile',
  FAQ: 'faq',
  TERMS_AND_CONDITIONS: 'terms-and-conditions',
  SIGN_OUT: 'sign-out',
};
export default function Header() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const triggerNotificationRef = useRef<HTMLButtonElement>();
  const triggerUserRef = useRef<HTMLButtonElement>();
  const navigate = useNavigate();
  const userPreferences = useRecoilValue(userPreferencesState);
  const uiStateValue = useRecoilValue(uiState);

  const { show: showUserMenu } = useContextMenu({
    id: 'user-context-menu',
  });

  const { show: showNotificationsMenu } = useContextMenu({
    id: 'notifications-context-menu',
  });

  const overviewNavigate = () => {
    if (!userPreferences.selectedSdpId || !userPreferences.selectedOverviewMeterTab) {
      navigate(`${routes.OVERVIEW.path}${window.location.search}`);
    } else if (uiStateValue.hasFatalError) {
      // If there is a fatal error, navigate to the overview page without any parameters and force reload
      navigate(routes.OVERVIEW.path);
      navigate(0);
    } else {
      navigate(
        routes.withArgs(`${routes.OVERVIEW.path}${routes.OVERVIEW.children.OVERVIEW_DETAIL.path}`, {
          ':id': EanURL.encrypt(userPreferences.selectedSdpId) as string,
          ':tabId': userPreferences.selectedOverviewMeterTab,
        })
      );
    }
  };

  function handleClickUserMenu(event: any) {
    const { left, bottom } = triggerNotificationRef.current?.getBoundingClientRect() as any;
    showUserMenu({
      event,
      position: {
        x: left,
        y: bottom + 8, // Adjust the y position to avoid overlap with the button
      },
    });
  }
  const handleItemClick = ({ id }: ItemParams<any, any>) => {
    switch (id) {
      case MENU_ACTIONS.SIGN_OUT:
        navigate(`${routes.SIGN_OUT.path}${window.location.search}`);
        break;
      case MENU_ACTIONS.FAQ:
        window.open(t('common.faq.link'), '_blank', 'noreferrer');
        break;
      case MENU_ACTIONS.TERMS_AND_CONDITIONS:
        navigate(`${routes.TERMS_AND_CONDITIONS.path}${window.location.search}`, {
          state: { hasFooter: false },
        });
        break;
      case MENU_ACTIONS.PROFILE:
        navigate(`${routes.PROFILE.path}${window.location.search}`, {});
        break;
    }
  };

  const handleClickBranding = () => {
    overviewNavigate();
  };

  const handleClickNotificationMenu = (event: any) => {
    if (location.pathname === routes.NOTIFICATIONS.path) {
      // If already on notifications page, do nothing
      return;
    }
    const { left, bottom } = triggerNotificationRef.current?.getBoundingClientRect() as any;
    showNotificationsMenu({
      event,
      position: {
        x: left,
        y: bottom + 8, // Adjust the y position to avoid overlap with the button
      },
    });
  };

  return (
    <header className="header">
      <img
        className="header__logo"
        onClick={handleClickBranding}
        data-test-id="branding-logo"
        src={visualLogo}
        alt="branding"
      />
      <div className="header__actions">
        <div
          data-test-id="user-avatar"
          className="header__user-avatar"
          ref={triggerUserRef as any}
          onClick={handleClickUserMenu}
        >
          {user ? (
            <Avatar name={`${user.firstName} ${user.lastName}`} size="44" round />
          ) : (
            ![routes.TERMS_AND_CONDITIONS.path].includes(location.pathname) && (
              <Skeleton count={1} width={44} height={44} borderRadius="50%" />
            )
          )}
          <Menu id="user-context-menu" preventDefaultOnKeydown={false}>
            <Item id={MENU_ACTIONS.PROFILE} onClick={handleItemClick}>
              <div className="contexify_item__icon" data-test-id="user-avatar-profile">
                <ProfileIcon />
              </div>
              {t('common.profile')}
            </Item>
            <Item id={MENU_ACTIONS.FAQ} onClick={handleItemClick}>
              <div className="contexify_item__icon" data-test-id="user-avatar-faq">
                <QuestionIcon />
              </div>
              {t('common.faq')}
            </Item>
            <Item id={MENU_ACTIONS.TERMS_AND_CONDITIONS} onClick={handleItemClick}>
              <div className="contexify_item__icon" data-test-id="user-avatar-terms-and-conditions">
                <DocumentIcon />
              </div>
              {t('common.terms.and.conditions')}
            </Item>
            <Item id={MENU_ACTIONS.SIGN_OUT} onClick={handleItemClick}>
              <div className="contexify_item__icon" data-test-id="user-avatar-signout">
                <ExitIcon />
              </div>
              {t('common.sign.out')}
            </Item>
          </Menu>
        </div>
        {user && (
          <div
            className="header__notifications"
            ref={triggerNotificationRef as any}
            data-test-id="notification-button"
            onClick={handleClickNotificationMenu}
          >
            <NotificationsCounter />
            <Menu
              id="notifications-context-menu"
              preventDefaultOnKeydown={false}
              data-test-id="notification-panel"
            >
              <Item className="notifications-context-menu__item" onClick={handleItemClick}>
                <NotificationsPanel />
              </Item>
            </Menu>
          </div>
        )}
      </div>
    </header>
  );
}
